// @flow
import * as React from 'react';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import { formActions, mergeActionsToProps } from 'redux-pure-form';

import EventContainer from './calendar.event-container.js';
import DateSelect from './calendar.event-date-select.js';
import Select from '../../form/select.js';
import GroupedSelect from '../../form/grouped-select.js';
import CustomFields from './calendar.event-custom-fields.js';
import {Time} from './calendar.event-view.js';
import Timepicker from "../../common/timepicker/timepicker";
import DurationPicker from "../../common/durationpicker/durationpicker";
import { isUserEventInstructor } from "./utils/permissions";

import {
  getActivityTypesOptions,
  getCustomFields,
  getParamsForEventAction,
  getRecurringSettings,
  isOrgOverdue,
  paramsToHash
} from '../calendar.model.js';

import {
  addEvent,
  addMaintenance,
  checkEventParams,
  saveEvent,
  saveMaintenance
} from '../calendar-day.actions.js';

import {
  AIR_EVENT_TYPE,
  chageRecurringOptions,
  EVENT_ADD,
  EVENT_EDIT,
  EVENT_RECURRING,
  EVENT_RESOURCE,
  MAINTENANCE_EVENT_TYPE
} from '../calendar.consts.js';

import {
  selectEventFirstPilotOptions,
  selectEventInstructorOptions,
  selectEventResourcesOptions,
  selectEventSICOptions,
  selectEvetnOrgOptions,
  selectFilterLocGroupedOptions
} from '../calendar.selectors.js';

import { dateToTime, timeToUnix, unixToTime, numberToTime, timeToNumber, unixToNumber } from "../../../utils/date";
import {isObjectEmpty} from "../../../utils/object";
import {newDate} from '../calendar.model.js';

type OwnProps = {|
  switchModals: Function,
  edit: boolean,
  inFlight: boolean
|};

type MappedState = {|
  UIMessages: Messages,
  URL: URL
|};

type Props = {|
  ...OwnProps,
  ...MappedState
|};

type State = {
  departureTime: *,
  flightTime: *,
  expectedArrivalTime: *,
  enableTrackFlight: boolean,
};

class EventAdd extends React.Component<Props, State> {
  static footer = true;

  constructor(props: Props) {
    super(props);

    this.timer = null;
    this.submitTimeout = null;

    this.handleEventTypeChange = this.handleChange.bind(this, 'eventForm.type');
    this.handleOrgChange = this.handleChange.bind(this, 'eventForm.organizationUnique');
    this.handleLocChange = this.handleChange.bind(this, 'eventForm.locationID');
    this.handlePilotChange = this.handleChange.bind(this, 'eventForm.pilotID');
    this.handleSICChange = this.handleChange.bind(this, 'eventForm.secondInComandID');
    this.handleInstructorChange = this.handleChange.bind(this, 'eventForm.instructorID');
    this.handleSICClear = this.handleClear.bind(this, 'eventForm.secondInComandID');
    this.handleInstructorClear = this.handleClear.bind(this, 'eventForm.instructorID');
    this.handleEventsForAction = this.handleChange.bind(this, 'eventForm.eventsForAction');
    this.handleActivityTypeChange = this.handleChange.bind(this, 'eventForm.activityType');
    this.handleCommentsChange = this.handleCommentsChange.bind(this);

    this.handleResourcesAdd = this.handleResourcesChange.bind(this, 'add');
    this.handleResourcesDelete = this.handleResourcesChange.bind(this, 'delete');

    this.handleRecurring = this.handleRecurring.bind(this);
    this.handleResourceClick = this.handleResourceClick.bind(this);

    const {event, user, organization} = props;
    const enableTrackFlight = organization.organizationSettings.enableTrackFlight === "1";

    if (!isObjectEmpty(event)) {
      const {
        departureTime,
        weightAndBalance,
        expectedArrivalTime,
      } = event;

      const weightAndBalanceObject = weightAndBalance ? JSON.parse(weightAndBalance) : {};

      let flightTimeInUnix;

      if (typeof weightAndBalanceObject === 'object' && weightAndBalanceObject !== null) {
        flightTimeInUnix = numberToTime(weightAndBalanceObject.eventDuration);
      } else {
        flightTimeInUnix = unixToTime((user.eventDefaultLength) * 1000);
      }

      const eventTimezone = event && event.timeZone ? event.timeZone[event.timeZoneID] : {offset: 0};

      const safeExpectedArrivalTime = expectedArrivalTime
        ? newDate(expectedArrivalTime, eventTimezone.offset)
        : new Date();

      const safeDepartureTime = departureTime
        ? newDate(departureTime, eventTimezone.offset)
        : new Date();

      this.state = {
        enableTrackFlight,
        departureTime: safeDepartureTime,
        flightTime: flightTimeInUnix,
        expectedArrivalTime: safeExpectedArrivalTime
      }
    }
  }

  timer: ?number;
  submitTimeout: ?number;

  state = {
    enableTrackFlight: false,
    departureTime: null,
    flightTime: null,
    expectedArrivalTime: null,
  }

  componentWillUnmount() {
    if (this.timer || this.submitTimeout) {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.submitTimeout);
    }
  }

  handleCommentsChange(e) {
    const { name, value } = e.currentTarget;
    this.props.fieldAttrs.onChange(name, value);
    if (this.timer) {
      window.clearTimeout(this.timer);
    }
    this.timer = window.setTimeout(this.props.checkEventParams, 3000);
    return true;
  }

  handleSubmit = (e) => {
    const {
      eventForm,
      saveEvent,
      saveMaintenance,
      addEvent,
      addMaintenance,
      URL
    } = this.props;

    e.preventDefault();
    if (URL.eventParams.action === EVENT_EDIT) {
      const {flightTime, expectedArrivalTime, departureTime} = this.state;

      const { weightAndBalance, inFlight } = eventForm;

      let params = {
        flightTime: timeToNumber(flightTime),
        departureTime: departureTime.toString(),
        expectedArrivalTime: expectedArrivalTime.toString()
      };

      if (inFlight === "0") {
        const eventDurationInUnix = (eventForm.finishTime - eventForm.startTime) * 1000;
        const weightAndBalanceObject = weightAndBalance ? JSON.parse(weightAndBalance) : {};

        params.flightTime = unixToNumber(eventDurationInUnix);

        if (typeof weightAndBalanceObject === 'object' && weightAndBalanceObject !== null) {
          weightAndBalanceObject.eventDuration =  params.flightTime;
          params.weightAndBalance = JSON.stringify(weightAndBalanceObject)
        }
      }

      return eventForm.type === AIR_EVENT_TYPE
        ? saveEvent(params)
        : saveMaintenance();
    }

    if (!this.submitTimeout) {
      this.submitTimeout = window.setTimeout(this.removeSubmitDisabling, 5000);

      return eventForm.type === AIR_EVENT_TYPE
        ? addEvent()
        : addMaintenance();
    }
  }

  removeSubmitDisabling = () => {
    window.clearTimeout(this.submitTimeout);
  }

  handleResourceClick(e) {
    const {switchModals, URL} = this.props;
    const {element} = e.currentTarget.dataset;

    const nextBreadcrumbs = URL.eventBreadcrumbs.concat({
      ...URL.eventParams,
      action: EVENT_RESOURCE,
      element
    });

    switchModals(
      EVENT_RESOURCE,
      {...URL.eventParams, element},
      nextBreadcrumbs
    );
  }

  handleClear(name) {
    const { fieldAttrs, checkEventParams } = this.props;
    fieldAttrs.onChange(name, null);
    checkEventParams();
  }

  handleChange(name, val) {
    const {
      fieldAttrs,
      checkEventParams,
      eventForm,
      locOptions,
      customFieldsList,
      customFields,
      paramsToHash,
      scrollY,
      resources,
    } = this.props;

    const value =
      val && typeof val === 'object' && val.currentTarget ? val.currentTarget.value : val;

    if (value !== undefined) fieldAttrs.onChange(name, value);

    if (value === null || value === '' || value === "0" || value === 0) return checkEventParams(); // clear button => val = null, empty string, or 0

    let org = eventForm.organizationUnique;
    let location = eventForm.locationID;
    let eventCustomFields = eventForm.customFields;

    switch (true) {
      case name === 'eventForm.type':
        const { type } = val.currentTarget.dataset;
        let nextValue = eventForm.type;
        if (type === AIR_EVENT_TYPE) {
          if (nextValue === AIR_EVENT_TYPE) return false;
          nextValue = AIR_EVENT_TYPE;
        }
        if (type === MAINTENANCE_EVENT_TYPE) {
          if (nextValue === MAINTENANCE_EVENT_TYPE) return false;
          nextValue = MAINTENANCE_EVENT_TYPE;
        }
        fieldAttrs.onChange('eventForm.type', nextValue);
        fieldAttrs.onChange(
          'eventForm.resources',
          eventForm.resources.length ? [eventForm.resources[0]] : []
        );
        break;

      case name === 'eventForm.organizationUnique' || name === 'eventForm.locationID':
        // ---------- organization change location ---------- //
        if (name === 'eventForm.organizationUnique') {
          if (
            locOptions[value] &&
            locOptions[value].length &&
            locOptions[value][1] &&
            locOptions[value][1].value
          ) {
            location = locOptions[value][1].value;
          } else {
            location = '';
          }
          org = value;
          eventCustomFields = getCustomFields(customFieldsList, customFields, org);
        } else {
          location = value;
        }
        fieldAttrs.onChange({
          eventForm: {
            ...eventForm,
            pilotID: '',
            secondInComandID: '',
            instructorID: '',
            resources: [],
            locationID: location,
            organizationUnique: org,
            customFields: eventCustomFields,
            activityType: undefined
          }
        });
        const params = getParamsForEventAction('event-add', {
          ...eventForm,
          organizationUnique: org,
          org,
          locationID: location,
          location
        });
        paramsToHash(params, scrollY);
        break;

      case name === 'eventForm.pilotID' && value === eventForm.secondInComandID:
        // ---------- next pilot reset used SIC pilot ---------- //
        fieldAttrs.onChange('eventForm.secondInComandID', null);
        break;

      case name === 'eventForm.pilotID' &&
        eventForm.instructorID &&
        value === resources[eventForm.instructorID].userID:
        // ---------- next pilot reset used instructor ---------- //
        fieldAttrs.onChange('eventForm.instructorID', null);
        break;

      case name === 'eventForm.secondInComandID' &&
        eventForm.instructorID &&
        value === resources[eventForm.instructorID].userID:
        // ---------- next SIC pilot reset used instructor ---------- //
        fieldAttrs.onChange('eventForm.instructorID', null);
        break;

      case name === 'eventForm.resources':
        fieldAttrs.onChange('eventForm.resources', value.value);
        break;
    }

    return checkEventParams(name === 'eventForm.locationID', location, org);
  }

  handleResourcesChange(type, value) {
    let nextResources;
    if (type === 'add') {
      if (this.props.eventForm.type === MAINTENANCE_EVENT_TYPE) {
        nextResources = [
          value.value,
          ...this.props.eventForm.resources.slice(1, this.props.eventForm.resources.length)
        ];
      } else {
        nextResources = [value.value, ...this.props.eventForm.resources];
      }
    } else {
      const { id } = value.currentTarget.dataset;
      const index = this.props.eventForm.resources.indexOf(id);
      nextResources = [
        ...this.props.eventForm.resources.slice(0, index),
        ...this.props.eventForm.resources.slice(index + 1)
      ];
    }

    this.props.fieldAttrs.onChange('eventForm.resources', nextResources);

    return this.props.checkEventParams();
  }

  handleRecurring(e) {
    const { forceOpen } = e.currentTarget.dataset;
    const nextState = forceOpen ? '1' : this.props.eventForm.recurring === '1' ? '0' : '1';
    this.props.fieldAttrs.onChange({
      eventForm: {
        ...this.props.eventForm,
        recurring: nextState,
        recurringAction: this.props.URL.eventParams.action === EVENT_EDIT ? 'save' : 'add',
        recurringEndDate:
          nextState === '1'
            ? this.props.eventForm.recurringEndDate.toString().length
              ? this.props.eventForm.recurringEndDate
              : parseInt(this.props.eventForm.startTime, 10) + 30 * 24 * 60 * 60
            : this.props.eventForm.startTime
      }
    });

    let nextBreadcrumbs;
    if (nextState === '1') {
      nextBreadcrumbs = this.props.URL.eventBreadcrumbs.concat({
        ...this.props.URL.eventParams,
        action: EVENT_RECURRING
      });
      this.props.switchModals(EVENT_RECURRING, this.props.URL.eventParams, nextBreadcrumbs);
    }
    return this.props.checkEventParams();
  }

  onTimeChangeWithDep = (rootValue, timeValue) => {
    const {departureTime} = this.state;

    if (rootValue === "flightTime") {
      const unixFlightTime = timeToUnix(timeValue);
      const departureTimeUnix = departureTime.getTime();
      const expectedUnixTime = departureTimeUnix + unixFlightTime;
      const expectedArrivalTime = new Date(expectedUnixTime);
      this.setState({
        flightTime: timeValue,
        expectedArrivalTime: expectedArrivalTime,
      }, () => {
        const {fieldAttrs, eventForm} = this.props;

        fieldAttrs.onChange({
          eventForm: {
            ...eventForm,
            flightTime: timeToNumber(timeValue),
            departureTime: departureTime.toString(),
            expectedArrivalTime: expectedArrivalTime.toString(),
          }
        });
      })
    } else if (rootValue === "expectedArrivalTime") {
      const [hours, minutes] = timeValue.split(":");
      const unixDepartureTime = departureTime.getTime();
      const newExArrivalTime = new Date(unixDepartureTime);

      newExArrivalTime.setHours(hours, minutes)

      let flightTime = newExArrivalTime.getTime() - unixDepartureTime;

      if (flightTime < 0) {
        newExArrivalTime.setDate(newExArrivalTime.getDate() + 1)
        flightTime =  newExArrivalTime.getTime() - unixDepartureTime;
      }
      const time = unixToTime(flightTime);

      this.setState({
        flightTime: time,
        expectedArrivalTime: newExArrivalTime,
      }, () => {
        const {fieldAttrs, eventForm} = this.props;

        fieldAttrs.onChange({
          eventForm: {
            ...eventForm,
            flightTime: timeToNumber(time),
            departureTime: departureTime.toString(),
            expectedArrivalTime: newExArrivalTime.toString()
          }
        });
      })
    }
  }

  onClearValue = () => {
    const {fieldAttrs, eventForm, checkEventParams} = this.props;

    fieldAttrs.onChange({
      eventForm: {
        ...eventForm,
        pilotID: "0",
      }
    });
    checkEventParams();
  }

  render() {
    const {
      URL,
      edit = false,
      inFlight = false,
      completed = false,
      user = {},
      event,
      eventForm,
      timezones,
      accessgroups,
      locations,
      instructors,
      resources,
      organizationsAccess,
      eventModalData,
      activityTypes,
      activeActivityTypesList,
      fieldAttrs,
      organization
    } = this.props;


    const isUserInstructor = isUserEventInstructor(eventForm, user, resources);
    const pilotOptional = organization.organizationSettings.pilotOptional === "1";

    const noDefaultPilot = pilotOptional && (isUserInstructor ? isUserInstructor : accessgroups.calendarManageAnothers);

    const {flightTime, expectedArrivalTime, enableTrackFlight} = this.state;

    if (URL.eventParams.action === EVENT_EDIT && !eventForm.id) return null;

    const timezone = locations[eventForm.locationID]
      ? timezones[locations[eventForm.locationID].timeZoneID]
      : {};

    const globalTimezone = this.props.timezone;

    const recurringErrorsLength = eventModalData.recurringErrors
      ? Object.keys(eventModalData.recurringErrors).length
      : 0;

    const orgActivityTypes = getActivityTypesOptions(
      activeActivityTypesList,
      activityTypes,
      eventForm.organizationUnique
    );

    const activityType = activityTypes[eventForm.activityType];

    return (
      <EventContainer>
        <form onSubmit={this.handleSubmit}>
          <div className="col-sm-5">
            {edit || inFlight || completed ? (
              <div className="from-group">
                <label>Organization</label>{' '}
                <p className="form-control-static">{eventForm.organizationName}</p>
              </div>
            ) : (
              <span>
                <Select
                  pretty
                  name="organizationUnique"
                  id="organizationUnique"
                  className="form-control input-sm"
                  title="Select Organization"
                  label="Organization"
                  options={this.props.orgOptions}
                  value={eventForm.organizationUnique}
                  onChange={(option) => this.handleOrgChange(option.value)}
                  required
                />
                {isOrgOverdue(eventForm.organizationUnique, organizationsAccess) ? (
                  <div>
                    <br />
                    <p className="alert alert-danger">
                      <span className="icon block" />
                      The organization is overdue
                    </p>
                  </div>
                ) : null}
              </span>
            )}

            {edit || inFlight || completed ? (
              <div className="from-group">
                <label>Location</label>{' '}
                <p className="form-control-static">
                  {eventForm.locationName}{' '}
                  <span className="text-muted">
                    [ {timezone.abbr} {timezone.name} {timezone.offsetString} ]
                  </span>
                </p>
              </div>
            ) : (
              <span>
                <Select
                  pretty
                  name="locationID"
                  id="locationID"
                  className="form-control input-sm"
                  title="Select Location"
                  label="Location"
                  options={
                    this.props.locOptions[eventForm.organizationUnique]
                      ? this.props.locOptions[eventForm.organizationUnique].slice(1) // delete option org grouping
                      : []
                  }
                  value={eventForm.locationID}
                  onChange={(option) => this.handleLocChange(option.value)}
                  required
                />
                {eventForm.locationID && eventForm.locationID.length ? (
                  <span className="help-block clean text-muted">
                    {' '}
                    Location Timezone: {timezone.abbr} {timezone.name} {timezone.offsetString}
                  </span>
                ) : null}
              </span>
            )}
            {!edit && !inFlight && !completed ? (
              <div className="form-group">
                <label>Event Type</label>{' '}
                <div className="btn-group">
                  <label
                    className={`btn btn-default btn-sm z-index-0 ${
                      eventForm.type === AIR_EVENT_TYPE ? 'active' : ''
                    }`}
                    data-type={AIR_EVENT_TYPE}
                    onClick={this.handleEventTypeChange}
                  >
                    <span className="icon plane-air text-muted" /> Regular
                  </label>
                  {URL.eventParams.org &&
                  organizationsAccess[URL.eventParams.org] &&
                  organizationsAccess[URL.eventParams.org].accessGroups.maintenanceManage ? (
                    <label
                      className={`btn btn-default btn-sm z-index-0 ${
                        eventForm.type === MAINTENANCE_EVENT_TYPE ? 'active' : ''
                      }`}
                      data-type={MAINTENANCE_EVENT_TYPE}
                      onClick={this.handleEventTypeChange}
                    >
                      <span className="icon maintenance text-muted" /> Maintenance
                    </label>
                  ) : null}
                </div>
              </div>
            ) : null}

            {eventForm.type === AIR_EVENT_TYPE ? (
              <div>
                {!inFlight && !completed ? (
                  <span id="pilot-id-select">
                    <Select
                      pretty
                      clearable={false}
                      name="eventForm.pilotID"
                      id="pilotID"
                      className="form-control input-sm"
                      placeholder="Select Pilot"
                      label="Pilot"
                      options={this.props.pilotOptions}
                      value={eventForm.pilotID}
                      onChange={this.handlePilotChange}
                      required={!noDefaultPilot}
                      onClear={noDefaultPilot && this.onClearValue}
                    />
                  </span>
                ) : (
                  <div className="from-group">
                    <label>Pilot</label>{' '}
                    <p className="form-control-static">
                      {eventForm.pilotName} {eventForm.pilotMiddlename} {eventForm.pilotSurname}{' '}
                      {eventForm.pilotID === user.id ? (
                        <span className="label label-default label-its-you">it's you</span>
                      ) : null}
                    </p>
                  </div>
                )}
                {!inFlight && !completed ? (
                  <Select
                    pretty
                    name="eventForm.secondInComandID"
                    id="secondInComandID"
                    className="form-control input-sm"
                    placeholder="Select Second Pilot"
                    label="SIC Pilot"
                    options={this.props.SICOptions}
                    value={eventForm.secondInComandID}
                    onChange={this.handleSICChange}
                    onClear={this.handleSICClear}
                  />
                ) : (
                  <div className="from-group">
                    <label>SIC Pilot</label>{' '}
                    {eventForm.secondInComand === '1' ? (
                      <p className="form-control-static">
                        {eventForm.sicPilotName} {eventForm.sicPilotMiddlename}{' '}
                        {eventForm.sicPilotSurname}{' '}
                        {eventForm.secondInComandID === user.id ? (
                          <span className="label label-default label-its-you">it's you</span>
                        ) : null}
                      </p>
                    ) : (
                      <p className="form-control-static">None</p>
                    )}
                  </div>
                )}
                {!inFlight && !completed ? (
                  <Select
                    pretty
                    name="eventForm.instructorID"
                    id="instructorID"
                    className="form-control input-sm"
                    placeholder="Select Instructor"
                    label="Instructor"
                    options={this.props.instructorOptions}
                    value={eventForm.instructorID}
                    onChange={this.handleInstructorChange}
                    onClear={this.handleInstructorClear}
                  />
                ) : (
                  <div className="from-group">
                    <label>Instructor</label>{' '}
                    {parseInt(eventForm.instructorID, 10) > 0 ? (
                      <p className="form-control-static">
                        {eventForm.instructorName} {eventForm.instructorMiddlename}{' '}
                        {eventForm.instructorSurname}{' '}
                        {instructors[`${eventForm.locationID}-${eventForm.instructorID}`] &&
                        resources[
                          instructors[`${eventForm.locationID}-${eventForm.instructorID}`]
                            .resourceID
                        ] &&
                        resources[
                          instructors[`${eventForm.locationID}-${eventForm.instructorID}`]
                            .resourceID
                        ].userID === user.id ? (
                          <span className="label label-default label-its-you">it's you</span>
                        ) : null}
                      </p>
                    ) : (
                      <p className="form-control-static">None</p>
                    )}
                  </div>
                )}
                <CustomFields formName="eventForm" checkParams />
                {activityType && activityType.trash == '0' ? (
                  <div className="activity-type-block">
                    <div
                      className="activity-type-preview"
                      style={{
                        backgroundColor: activityType.color
                      }}
                    />
                  </div>
                ) : null}
                {orgActivityTypes.length ? (
                  <span>
                    <Select
                      pretty
                      name="activityType"
                      id="activityType"
                      className="form-control input-sm"
                      title="Select activity type"
                      placeholder="Not set"
                      label="Activity Type"
                      options={orgActivityTypes}
                      value={eventForm.activityType || ''}
                      onChange={this.handleActivityTypeChange}
                    />
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="col-sm-7">
            {inFlight || completed ? (
              <span>
                <Time
                  label="Start Time"
                  user={user}
                  timezone={timezone}
                  globalTimezone={globalTimezone}
                  date={eventForm.startTime}
                />

                <Time
                  label="End Time"
                  user={user}
                  timezone={timezone}
                  globalTimezone={globalTimezone}
                  date={eventForm.finishTime}
                />
              </span>
            ) : (
              <span>
                <DateSelect period="start" />
                <DateSelect period="finish" />
              </span>
            )}

            {enableTrackFlight && inFlight && (
              <span>
                <div className={"form-group"}>
                  <label htmlFor="flightTime" className="time-label col-sm-5">
                    Flight Time
                  </label>
                  <DurationPicker
                    value={flightTime}
                    onChange={(time) => this.onTimeChangeWithDep("flightTime", time)}
                    required={true}
                    id={"flight-time"}
                    name={"flight-time"}
                  />
                </div>
                <div className={"form-group"}>
                  <label htmlFor="expectedArrivalTime" className="time-label col-sm-5">
                    Expected Arrival Time
                  </label>
                  <Timepicker
                    value={dateToTime(expectedArrivalTime)}
                    onChange={(time) => this.onTimeChangeWithDep("expectedArrivalTime", time)}
                    required={true}
                    id={"arrival-time"}
                    name={"arrival-time"}
                  />
                </div>
              </span>
            )}

            {URL.eventParams.org &&
            eventForm.type === AIR_EVENT_TYPE &&
            organizationsAccess[URL.eventParams.org].accessGroups.calendarManageRecurrentEvent ? (
              <div className="form-group">
                <label className="checkbox without-label">
                  <input
                    type="checkbox"
                    checked={eventForm.recurring === '1'}
                    disabled={inFlight || completed}
                    onChange={this.handleRecurring}
                  />
                  Recurring Event
                </label>
                {eventForm.recurring === '1' ? (
                  <div className="help-block clean">
                    <b>{getRecurringSettings(eventForm, eventForm.timezone.offset)}</b>
                    <br />
                    <b
                      className={`link--ajax ${
                        recurringErrorsLength ? 'text-danger' : 'text-primary'
                      }`}
                      data-force-open="1"
                      onClick={this.handleRecurring}
                    >
                      Edit the Sequence{' '}
                      {recurringErrorsLength ? (
                        <span>(Recurring Errors: {recurringErrorsLength})</span>
                      ) : (
                        <span>(no errors)</span>
                      )}
                    </b>
                  </div>
                ) : null}
              </div>
            ) : null}

            {URL.eventParams.org &&
            edit &&
            !completed &&
            eventForm.type === AIR_EVENT_TYPE &&
            eventForm.completed === '0' &&
            eventForm.recurring === '1' ? (
              <Select
                value={eventForm.eventsForAction}
                id="eventsForAction"
                label="Change Event"
                className="form-control input-sm"
                options={chageRecurringOptions}
                onChange={this.handleEventsForAction}
              />
            ) : null}

            {!inFlight && !completed ? (
              <div className="from-group">
                <label htmlFor="resources" className="without-label">
                  Add Resource
                </label>
                <GroupedSelect
                  searchable
                  clearable={false}
                  id="resources"
                  className="resources-select"
                  placeholder="Select Resource"
                  name="eventForm.resources"
                  options={this.props.resourcesOptions}
                  value={
                    eventForm.type === MAINTENANCE_EVENT_TYPE
                      ? [eventForm.resources[0]]
                      : eventForm.resources
                  }
                  onChange={this.handleResourcesAdd}
                  valueComponent={() => (
                    <span className="abs-select">
                      {eventForm.type === MAINTENANCE_EVENT_TYPE && eventForm.resources[0]
                        ? `${resources[eventForm.resources[0]].resourceTypeName}: ${resources[eventForm.resources[0]].resourceName}`
                        : 'Select Resource'}
                    </span>
                  )}
                />
              </div>
            ) : null}

            {inFlight || completed ? (
              <div className="from-group">
                <label htmlFor="recurring">Resources</label>
                {eventForm.resources.map(resID => {
                  const resource = event.resources.find(res => res.id === resID);
                  if (!resource) return null;

                  const isAirworthy = resource.statusInspections === 'airworthy';
                  const isUnairworthy = resource.statusInspections === 'unairworthy';

                  return eventForm.resourcesRestricted.indexOf(resID) === -1 &&
                    organizationsAccess[eventForm.organizationUnique] &&
                    organizationsAccess[eventForm.organizationUnique].accessGroups
                      .calendarManageAnothers ? (
                    <div key={`RES-${resID}`}>
                      <b>{resource.typeName}:</b>{' '}
                      {resource.aircraftID !== null || resource.simulatorID !== null ? (
                        <span
                          data-element={resource.aircraftID || resource.simulatorID}
                          onClick={this.handleResourceClick}
                        >
                          <span
                            className="link--ajax"
                            title={isAirworthy ? 'Airworthy' : isUnairworthy ? 'Unairworthy' : ''}
                          >
                            {resource.name}
                            {accessgroups.discrepanciesView && resource.unique === 'aircraft' ? (
                              <span>{` (Discrepancies: ${resource.discrepanciesCount})`}</span>
                            ) : null}
                          </span>
                          <span
                            className={`
                                      ${
                                        isAirworthy
                                          ? 'inspections-status icon checkmark-circle text-success'
                                          : ''
                                      }
                                      ${
                                        isUnairworthy
                                          ? 'inspections-status icon cancel-circled text-danger'
                                          : ''
                                      }
                                    `}
                            title={isAirworthy ? 'Airworthy' : isUnairworthy ? 'Unairworthy' : ''}
                          />
                        </span>
                      ) : (
                        <span>{resource.name}</span>
                      )}
                    </div>
                  ) : null;
                })}
              </div>
            ) : null}

            {eventForm.resources.length && !inFlight && !completed ? (
              <div className="form-group">
                <label>Resources</label>
                <ul className="list-unstyled list-buttons resources with-button">
                  {eventForm.resources.map((resID, i) => {
                    const resource = eventModalData.resources && eventModalData.resources[resID];
                    if (!resource) return null;
                    if (eventForm.type === MAINTENANCE_EVENT_TYPE && i > 0) return null;

                    const isAirworthy = resource.statusInspections === 'airworthy';
                    const isUnairworthy = resource.statusInspections === 'unairworthy';

                    return (
                      <li key={`${resID}-resource`} style={{ display: 'block' }}>
                        <button
                          type="button"
                          data-id={resID}
                          onClick={this.handleResourcesDelete}
                          className="btn btn-xs btn-default icon trash"
                        />
                        <div className="block-text">
                          <span>
                            <b>{resource.typeName}:</b> {resource.name}
                            {edit &&
                            resource.unique === 'aircraft' &&
                            accessgroups.discrepanciesView ? (
                              <span
                                className="link--ajax"
                                data-element={resource.aircraftID || resource.simulatorID}
                                onClick={edit ? this.handleResourceClick : null}
                              >
                                {' '}
                                (Discrepancies: {resource.discrepanciesCount})
                              </span>
                            ) : null}
                            <span
                              className={`
                                    ${
                                      isAirworthy
                                        ? 'inspections-status icon checkmark-circle text-success'
                                        : ''
                                    }
                                    ${
                                      isUnairworthy
                                        ? 'inspections-status icon cancel-circled text-danger'
                                        : ''
                                    }
                                  `}
                              title={isAirworthy ? 'Airworthy' : isUnairworthy ? 'Unairworthy' : ''}
                            />
                            <span className="text-danger">
                              {resource.used && !edit && !inFlight
                                ? resource.maintenance || ' resource is used'
                                : ''}
                            </span>
                            {resource.blockedBy.length ? (
                              <span>
                                <br />
                                <span className="text-danger">{resource.blockedBy.join(', ')}</span>
                              </span>
                            ) : null}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="comments required-label">Comments</label>
              <textarea
                id="comments"
                rows="4"
                name="eventForm.comments"
                className="form-control input-sm"
                value={eventForm.comments || ''}
                required={eventModalData.enforseComments === '1'}
                onChange={this.handleCommentsChange}
              />
            </div>

            {eventModalData.enableEventSharing === '1' && eventForm.type === AIR_EVENT_TYPE ? (
              <label className="checkbox">
                <input
                  type="checkbox"
                  value="1"
                  name="eventForm.share"
                  checked={eventForm.share === '1'}
                  {...fieldAttrs}
                />
                Invite other pilots to participate in this event
                <span className="help-block clean">
                  Users with <b>Pilot Role</b> will receive an email invitation to join in after you
                  check off this box and save the event
                </span>
              </label>
            ) : null}
          </div>
          <input id={EVENT_ADD} type="submit" style={{ display: 'none' }} />
          <input id={EVENT_EDIT} type="submit" style={{ display: 'none' }} />
        </form>
      </EventContainer>
    );
  }
}

const mapStateToProps: MapStateToProps<ReduxState, OwnProps, MappedState> = state => ({
  URL: state.URL,
  eventForm: state.eventForm,
  event: state.event,
  user: state.user,
  timezone: state.timezone,
  timezones: state.timezones,
  instructors: state.instructors,
  resources: state.resources,
  organization: state.organization,
  organizations: state.organizations,
  accessgroups: state.accessgroups,
  locations: state.locations,
  organizationsList: state.organizationsList,
  organizationsAccess: state.organizationsAccess,
  eventModalData: state.eventModalData,
  scrollY: state.userUI.scrollY,
  customFields: state.customFields,
  customFieldsList: state.customFieldsList,
  activityTypes: state.activityTypes,
  activeActivityTypesList: state.activeActivityTypesList,
  orgOptions: selectEvetnOrgOptions(state),
  locOptions: selectFilterLocGroupedOptions(state),
  pilotOptions: selectEventFirstPilotOptions(state),
  SICOptions: selectEventSICOptions(state),
  instructorOptions: selectEventInstructorOptions(state),
  resourcesOptions: selectEventResourcesOptions(state)
});

export default connect(
  mapStateToProps,
  {
    paramsToHash,
    checkEventParams,
    addEvent,
    addMaintenance,
    saveEvent,
    saveMaintenance,
    ...formActions
  },
  mergeActionsToProps
)(EventAdd);
